import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Email,
  Schedule,
  HelpOutline,
  Description,
  BugReport,
  Lightbulb,
  ContentCopy,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import BrandWordmark, { CHECK_NAVY, NOW_GRADIENT_TOP, NOW_GRADIENT_BOTTOM } from '../components/BrandWordmark';

const SUPPORT_EMAIL = 'support@check-now.co.uk';

const helpTopics = [
  {
    icon: HelpOutline,
    title: 'General questions',
    description: 'Accounts, billing, and platform overview',
  },
  {
    icon: BugReport,
    title: 'Technical issues',
    description: 'Login problems, sync errors, and app behaviour',
  },
  {
    icon: Description,
    title: 'Implementation',
    description: 'Setup, training, and best-practice guidance',
  },
  {
    icon: Lightbulb,
    title: 'Feature requests',
    description: 'Suggestions for improvements and new capabilities',
  },
];

const responseTimes = [
  { label: 'General enquiries', time: 'Within 1 business day' },
  { label: 'Technical issues', time: 'Within 4 hours on business days' },
  { label: 'Critical platform issues', time: 'Within 1 hour on business days' },
];

const Support: React.FC = () => {
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <BrandWordmark variant="h4" component="h1" />
          <Typography
            variant="h5"
            sx={{
              mt: 2,
              fontWeight: 700,
              color: CHECK_NAVY,
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: '-0.02em',
            }}
          >
            Support
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
            Our team can help with the admin portal, mobile app, and your Check-Now account.
          </Typography>
        </Box>

        {/* Primary contact */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(CHECK_NAVY, 0.1)}`,
            boxShadow: `0 12px 40px ${alpha(CHECK_NAVY, 0.08)}`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: 4,
              background: `linear-gradient(90deg, ${NOW_GRADIENT_TOP} 0%, ${NOW_GRADIENT_BOTTOM} 100%)`,
            }}
          />
          <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: alpha(NOW_GRADIENT_TOP, 0.1),
                mb: 2,
              }}
            >
              <Email sx={{ fontSize: 28, color: NOW_GRADIENT_BOTTOM }} />
            </Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600 }}>
              Email us
            </Typography>
            <Typography
              component="a"
              href={`mailto:${SUPPORT_EMAIL}`}
              variant="h5"
              sx={{
                display: 'block',
                mt: 1,
                fontWeight: 700,
                color: CHECK_NAVY,
                textDecoration: 'none',
                fontFamily: "'Montserrat', sans-serif",
                wordBreak: 'break-all',
                '&:hover': { color: NOW_GRADIENT_BOTTOM },
              }}
            >
              {SUPPORT_EMAIL}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: 400, mx: 'auto' }}>
              Include your business name and a clear description of your question so we can respond quickly.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                href={`mailto:${SUPPORT_EMAIL}`}
                startIcon={<Email />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  background: `linear-gradient(135deg, ${NOW_GRADIENT_TOP} 0%, ${NOW_GRADIENT_BOTTOM} 100%)`,
                  boxShadow: `0 2px 8px ${alpha(NOW_GRADIENT_BOTTOM, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${NOW_GRADIENT_TOP} 0%, ${CHECK_NAVY} 100%)`,
                  },
                }}
              >
                Send email
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleCopyEmail}
                startIcon={<ContentCopy />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: alpha(CHECK_NAVY, 0.2),
                  color: CHECK_NAVY,
                  '&:hover': { borderColor: NOW_GRADIENT_TOP, bgcolor: alpha(NOW_GRADIENT_TOP, 0.06) },
                }}
              >
                Copy address
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(CHECK_NAVY, 0.08)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: CHECK_NAVY, mb: 2 }}>
                  Support hours
                </Typography>
                <List disablePadding>
                  <ListItem disableGutters sx={{ py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Schedule sx={{ color: NOW_GRADIENT_BOTTOM }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Monday – Friday"
                      secondary="9:00 AM – 6:00 PM (GMT)"
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Email sx={{ color: NOW_GRADIENT_BOTTOM }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="United Kingdom"
                      secondary="Remote support for all customers"
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: CHECK_NAVY, mb: 1 }}>
                  Typical response times
                </Typography>
                <List dense disablePadding>
                  {responseTimes.map((item) => (
                    <ListItem key={item.label} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={item.label}
                        secondary={item.time}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(CHECK_NAVY, 0.08)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: CHECK_NAVY, mb: 2 }}>
                  What we can help with
                </Typography>
                <List disablePadding>
                  {helpTopics.map(({ icon: Icon, title, description }) => (
                    <ListItem key={title} disableGutters sx={{ py: 1, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>
                        <Icon sx={{ color: NOW_GRADIENT_BOTTOM }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={title}
                        secondary={description}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box
          sx={{
            textAlign: 'center',
            mt: 5,
            pt: 3,
            borderTop: `1px solid ${alpha(CHECK_NAVY, 0.1)}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/privacy-policy" sx={{ color: NOW_GRADIENT_BOTTOM, fontWeight: 500 }}>
              Privacy Policy
            </Link>
            {' · '}
            <Link component={RouterLink} to="/terms" sx={{ color: NOW_GRADIENT_BOTTOM, fontWeight: 500 }}>
              Terms &amp; Conditions
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Support;
